import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MultiHedge, RfqQuote, RfqSetup } from '@approot/models';
import { HedgeMode } from '@approot/services';
import { Utilities, clearEmptyValues, reformatDate } from '@approot/utilities';
export type HedgeRecord = Record<string, number | string>;
export type MifidChecksStatus =
  | 'ChecksInProgress'
  | 'GoodToTrade'
  | 'ChecksFailed';

export interface AdditionalRfqAttributes {
  shortName: string;
  longName: string;
  type: 'enum' | 'string' | 'boolean';
  readOnly: boolean;
  lambda: string;
  task: string;
  taskInputs?: string[];
  taskOutputName: string;
  requiredOnSubmitRequest: boolean;
  lockedOnSubmitRequest: boolean;
  requiredOnBookTrade: boolean;
  lockedOnBookTrade: boolean;
  disableOnNoneHedgeType: boolean;
  default?: string | boolean;
  values?: string[];
  forcePrimeBrokerToNoneExceptOnValues?: string[];
}

export interface IndicativeSpot {
  bid: string;
  ask: string;
  mid: string;
  flashOnChange: boolean;
}

export type HedgeExecutionMode =
  | 'Live - Forward Ref.'
  | 'DX - Forward Ref.'
  | 'Live - Spot + Pts Ref.'
  | 'Spot DX with Live pts';

export interface LiveHedgeRate {
  spotRefAsk: string;
  spotRefMid: string;
  spotRefBid: string;
  fwdPointAsk: string;
  fwdPointBid: string;
  fwdPointMid: string;
  fwdRateAsk?: string;
  fwdRateBid?: string;
  fwdRateMid?: string;
}

export function mapHedgeGroup(hedges: MultiHedge[]): HedgeRecord[] {
  return hedges
    .filter(hedge => hedge.productType !== 'None')
    .map((hedge: MultiHedge): HedgeRecord => {
      return {
        productType: hedge.productType.toLowerCase(),
        notional: hedge.amount
          ? Utilities.removeCommasConvertToNumber(hedge.amount)
          : null,
        position: hedge.offerDirection,
        strike: hedge.rate
          ? Utilities.removeCommasConvertToNumber(hedge.rate)
          : null,
        settlementDate: hedge.settlementDate
          ? reformatDate(hedge.settlementDate, 'YYYY-MM-DD')
          : null,
        expiryDate: hedge.expirationDate
          ? reformatDate(hedge.expirationDate, 'YYYY-MM-DD')
          : null,
        hedgeRateAsk: hedge.hedgeRateAsk,
        hedgeRateBid: hedge.hedgeRateBid,
        hedgeRateMid: hedge.hedgeRateMid,
        fwdRateAsk: hedge.fwdRateAsk,
        fwdRateBid: hedge.fwdRateBid,
        fwdRateMid: hedge.fwdRateMid,
        spotRefAsk: hedge.spotRefAsk,
        spotRefBid: hedge.spotRefBid,
        spotRefMid: hedge.spotRefMid,
        fwdPointAsk: hedge.fwdPointAsk,
        fwdPointBid: hedge.fwdPointBid,
        fwdPointMid: hedge.fwdPointMid,
        execution: hedge.executionMode
      };
    })
    .map(hedge => {
      return clearEmptyValues<HedgeRecord>(hedge);
    });
}

export function hedgeRateValidator(
  control: FormGroup
): ValidationErrors | null {
  // possible race
  if (!control?.parent) {
    return null;
  }
  const hedgeDirection = control.parent.get('hedgeDirection');
  const hedgeAmount = control.parent.get('hedgeAmount');
  if (hedgeDirection.enabled && hedgeAmount.enabled) {
    return null;
  }
  return control.value != null
    ? null
    : { invalidEntry: { value: control.value } };
}

export function groupValidator(control: FormGroup): ValidationErrors | null {
  const hedgeRate = control.get('hedgeRate');
  const hedgeDirection = control.get('hedgeDirection');
  const hedgeAmount = control.get('hedgeAmount');
  if (hedgeDirection.enabled && hedgeAmount.enabled && hedgeRate.enabled) {
    const validAmountAndDirection =
      hedgeAmount.value !== '' && hedgeDirection.value;
    const validHedgeRate = hedgeRate.value != null;

    return (validHedgeRate && validAmountAndDirection) ||
      validAmountAndDirection ||
      (validHedgeRate &&
        hedgeAmount.value === '' &&
        hedgeDirection.value == null)
      ? null
      : { invalidEntry: { value: hedgeDirection.value } };
  }
  return null;
}

export function bankValidator(control: FormControl): ValidationErrors | null {
  return control.value.length
    ? null
    : { invalidEntry: { value: control.value } };
}

export function createSetupCtrls(
  hedge: string,
  isVolBased: boolean,
  isLegacyWhitelabel: boolean,
  useBlockTrades: boolean,
  useSpotInRfqPanel: boolean,
  isWhiteLabel: boolean,
  enableTwoWayCustomHedgeRateForNonDx: boolean,
  additionalRfqAttributes: AdditionalRfqAttributes[],
  genericMultiHedge: boolean
): Record<string, FormControl | FormGroup> {
  const isHedgeCustom = hedge === 'Custom Spot' || hedge === 'Custom Forward';
  const spotReg = /^\d+(\.\d+)?$/;

  let ctrls: Record<string, FormControl | FormGroup> = {
    account: new FormControl('', [Validators.required]),
    entity: new FormControl('-'),
    primeBroker: new FormControl('', [Validators.required]),
    premium: new FormControl('', [Validators.required]),
    premiumValue: new FormControl('', [Validators.required]),
    premiumDate: new FormControl(''),
    hedge: new FormControl<string>('', [Validators.required]),
    hedgeMode: new FormControl<HedgeMode>(isVolBased ? 'DX' : 'Live'),
    hedgeRate: new FormControl<string>(
      {
        value: null,
        disabled: !isHedgeCustom
      },
      [hedgeRateValidator]
    ),
    hedgeAmount: new FormControl({
      value: '',
      disabled: !isHedgeCustom
    }),
    hedgeCustomAmount: new FormControl(''),
    hedgeDirection: new FormControl({
      value: null,
      disabled: true
    }),
    hedgeDirectionBid: new FormControl({
      value: null,
      disabled: true
    }),
    banks: new FormControl([], [bankValidator])
  };

  if (enableTwoWayCustomHedgeRateForNonDx || genericMultiHedge) {
    ctrls = {
      ...ctrls,
      hedgeRates: new FormGroup(
        {
          hedgeRateBid: new FormControl('', [Validators.required]),
          hedgeRateAsk: new FormControl('', [Validators.required])
        },
        [
          hedgeRatesValidator.bind(this, {
            ask: 'hedgeRateAsk',
            bid: 'hedgeRateBid'
          })
        ]
      )
    };

    if (genericMultiHedge) {
      (ctrls.hedgeRates as FormGroup).addControl(
        'hedgeRateMid',
        new FormControl({ value: '', disabled: true }, [Validators.required])
      );
    }
  }

  if (isVolBased) {
    ctrls = {
      ...ctrls,
      premiumStyle: new FormControl('', [Validators.required])
    };
  }

  if (isWhiteLabel) {
    ctrls = {
      ...ctrls,
      indicativePremiumStyle: new FormControl('', [Validators.required])
    };
  }

  if (isLegacyWhitelabel) {
    ctrls = {
      ...ctrls,
      account: new FormControl(''),
      primeBroker: new FormControl(''),
      banks: new FormControl([])
    };
  }

  if (useBlockTrades) {
    ctrls = {
      ...ctrls,
      isBlockTrade: new FormControl<boolean>(null, [Validators.required]),
      subAccount: new FormControl({
        value: null,
        disabled: true
      })
    };
  }

  if (genericMultiHedge) {
    ctrls = {
      ...ctrls,
      execution: new FormControl('Live'),
      spotRefMethod: new FormControl('custom')
    };
  }

  if (useSpotInRfqPanel) {
    ctrls = {
      ...ctrls,
      spots: new FormGroup({
        spotBid: new FormControl('', [
          Validators.required,
          Validators.pattern(spotReg)
        ]),
        spotAsk: new FormControl('', [
          Validators.required,
          Validators.pattern(spotReg)
        ])
      })
    };
  }

  additionalRfqAttributes.forEach(attribute => {
    const defaultValue =
      attribute.default || (attribute.type === 'boolean' ? false : '');
    ctrls[attribute.shortName] = new FormControl(defaultValue);
  });
  return ctrls;
}

export function hedgeRatesValidator(
  fields: { ask: string; bid: string },
  control: FormGroup
): ValidationErrors | null {
  const values = control.value;
  const hedgeRateAsk = Utilities.cleanFormattedNumeric(values[fields.ask]);
  const hedgeRateBid = Utilities.cleanFormattedNumeric(values[fields.bid]);

  [fields.ask, fields.bid].forEach(field => {
    const ctrl = control.controls[field];
    ctrl.setErrors(
      Utilities.isNotBlank(ctrl.value) &&
        !isNaN(Utilities.cleanFormattedNumeric(ctrl.value))
        ? null
        : {
            invalidEntry: { value: control.controls[field].value }
          }
    );
  });

  if (hedgeRateAsk < hedgeRateBid) {
    control.controls[fields.ask].setErrors({
      invalidEntry: { value: control.controls[fields.ask].value }
    });
    control.controls[fields.bid].setErrors({
      invalidEntry: { value: control.controls[fields.bid].value }
    });
    control.controls[fields.ask].markAsTouched();
    control.controls[fields.bid].markAsTouched();
    return { invalidEntry: { value: control.value } };
  }

  return null;
}

export function isQuoteOutdated(
  quote: RfqQuote,
  setup: RfqSetup,
  hedges: MultiHedge[]
): void {
  if (setup.hedge === 'Live Auto Hedger') {
    quote.outdated = false;
    return;
  }

  if (
    (setup.execution === 'Live' || setup.execution === 'DX') &&
    setup.spotRefMethod === 'custom' &&
    !quote.hedgeQuotes &&
    quote.quoteReference
  ) {
    quote.outdated = true;
    return;
  }

  if (
    setup.spotRefMethod === 'custom' &&
    hedges?.[0]?.executionMode === 'DX - Forward Ref.'
  ) {
    const hedgeRatesNotSame = quote.hedgeQuotes?.find(hedgeQuote => {
      const hedge = hedges?.find(
        hedge =>
          reformatDate(hedge.settlementDate, 'YYYY-MM-DD') ===
          hedgeQuote.hedgeDeliveryDate
      );
      return !hedge
        ? null
        : +hedge.fwdRateMid !== hedgeQuote.hedgeRate &&
            hedge.executionMode === 'DX - Forward Ref.';
    });

    if (hedgeRatesNotSame) {
      quote.outdated = true;
      return;
    }
  }

  if (
    !quote.hedgeQuotes ||
    (setup.hedge !== 'Spot Hedge' && setup.hedge !== 'Forward Hedge') ||
    (setup.hedge === 'Forward Hedge' && hedges?.length > 1)
  ) {
    quote.outdated = false;
    return;
  }

  if (setup.execution === 'Live' && setup.spotRefMethod === 'custom') {
    const ask = +setup.hedgeRates.hedgeRateAsk;
    const bid = +setup.hedgeRates.hedgeRateBid;
    quote.outdated = !quote.hedgeQuotes.find(
      hedge => +hedge.hedgeRate === ask || +hedge.hedgeRate === bid
    );
    return;
  } else if (setup.execution === 'DX' && setup.spotRefMethod === 'custom') {
    quote.outdated = !quote.hedgeQuotes.find(
      hedge => hedge.hedgeRate === +setup.hedgeRates.hedgeRateMid
    );
    return;
  }
  quote.outdated = false;
}
